import { GraphQLClient, ClientError } from 'graphql-request';
import { environment } from 'environment';
import { setHasAccess, setIsLoggedIn } from 'shared/store/appStore';
import { getAuthToken } from 'shared/hooks/initApp/functions/getAuthToken';

const state = (process.env.REACT_APP_ENV || 'production') as keyof typeof environment;
const client = new GraphQLClient(environment[state].api);

const redirectToLogin = () => {
  setHasAccess(false);
  setIsLoggedIn(false);
};

/**
 * The Cognito session is gone or expired. The user has already been sent back to the
 * login screen, so this is expected control flow rather than a defect — `beforeSend`
 * in `shared/sentry.ts` drops it by `name` instead of by message text.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

/**
 * The request never reached the API (shop-floor wifi drop, DNS, blocked CORS preflight).
 * Transient by nature and retried by the next user action, so it is also filtered out
 * of Sentry — see `beforeSend` in `shared/sentry.ts`.
 */
export class NetworkUnavailableError extends Error {
  constructor(cause?: unknown) {
    super('Network request failed');
    this.name = 'NetworkUnavailableError';
    (this as { cause?: unknown }).cause = cause;
  }
}

/** True when the transport failed before any HTTP status was produced. */
const isTransportFailure = (err: unknown): boolean => {
  const status = (err as ClientError)?.response?.status;
  if (status !== undefined) return false;
  return (
    err instanceof TypeError ||
    err instanceof SyntaxError ||
    (err as Error)?.message?.includes?.('Network request failed') === true
  );
};

/** True when the request failed due to auth (401, CORS-blocked 401, or non-JSON 401 body). */
const isAuthFailure = (err: unknown): boolean => {
  const status = (err as ClientError)?.response?.status;
  if (status === 401) return true;
  if (status !== undefined) return false;
  // A transport failure is indistinguishable from a CORS-blocked 401 — but while the
  // browser reports itself offline there is nothing to re-authenticate against, so
  // don't throw the user out of a half-finished distribution round.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  return isTransportFailure(err);
};

export const gqlClient = async (gql: string, variables: Record<string, unknown> = {}) => {
  let token: string | null = null;
  try {
    token = await getAuthToken();
  } catch {
    redirectToLogin();
    throw new SessionExpiredError();
  }

  if (!token) {
    redirectToLogin();
    throw new SessionExpiredError();
  }

  try {
    return await client.request(gql, variables, {
      Authorization: encodeURIComponent(token),
    });
  } catch (err) {
    if (isAuthFailure(err)) redirectToLogin();
    if (isTransportFailure(err)) throw new NetworkUnavailableError(err);
    throw err;
  }
};
